import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "../icons";
import classNames from "classnames";
import { ThemeProviderContext } from "../ThemeProvider";
import { textSizeExtras, textSizeOptions } from "./TextSizeUtils";
import { BaseSelect } from "../select/BaseSelect";
const CustomListItemContent = ({ value, defaultTextSize, }) => {
    const theme = useContext(ThemeProviderContext);
    if (!value)
        return null;
    return (_jsxs("div", { className: classNames("flex items-baseline p-0 w-full", textSizeExtras[value].styles), children: [_jsx("span", { children: theme.translate(textSizeExtras[value].labelKey) }), value === defaultTextSize && (_jsx("span", { className: "text-[14px] ml-0.5 text-text-subdued font-normal self-center leading-[16px]", children: theme.translate("components.rich_text_editor.text_size.default") }))] }));
};
export const TextSizeDropdown = ({ editor, defaultTextSize = "body14", menuOffset, }) => {
    const theme = useContext(ThemeProviderContext);
    const [isOpen, setIsOpen] = useState(false);
    const [currentTextSize, setCurrentTextSize] = useState(defaultTextSize);
    useEffect(() => {
        const filterTextSizeClassNames = (classAttr) => {
            return classAttr
                .map((c) => (c === null || c === void 0 ? void 0 : c.split(" ")) || "")
                .flat()
                .filter((c) => c.startsWith("text-size-"));
        };
        const getTextSizeClassnamesWithinSelection = () => {
            // This gets a little complicated since a paragraph can have a text size that differs
            // from the text size of a span within it. One or both (or more) text sizes may be in
            // use within the current selection
            var _a;
            if (!editor)
                return [];
            const from = editor.view.state.selection.$from;
            const to = editor.view.state.selection.$to;
            const classAttrWithinSelection = [];
            const paragraphClassAttrWithinSelection = [];
            // If the sizedSpan is in the storedMarks list then it will be created and
            // wrapped around the next input, so that's the current text size selection
            const storedSizeSpan = (_a = editor.state.storedMarks) === null || _a === void 0 ? void 0 : _a.find((m) => m.type.name === "sizedSpan");
            if (storedSizeSpan) {
                classAttrWithinSelection.push(storedSizeSpan.attrs.class);
            }
            else {
                // Otherwise we loop through all the selected nodes
                editor.state.doc.nodesBetween(from.pos === to.pos ? from.pos - 1 : from.pos, to.pos, ({ type, marks, attrs }, _pos, parentNode) => {
                    if (type.name === "text") {
                        // If the text node is wrapped by a sizedSpan, then that has a text size class we care about
                        // Otherwise, that text size is determined by it's paragraph so we'll use that instead
                        const sizedSpan = marks.find(({ type }) => type.name === "sizedSpan");
                        classAttrWithinSelection.push(sizedSpan ? sizedSpan.attrs.class : parentNode === null || parentNode === void 0 ? void 0 : parentNode.attrs.class);
                    }
                    else if (type.name === "paragraph") {
                        paragraphClassAttrWithinSelection.push(attrs.class);
                    }
                });
                // If there were no text nodes selected, then just use the selected paragraph classes
                if (classAttrWithinSelection.length === 0) {
                    classAttrWithinSelection.push(...paragraphClassAttrWithinSelection);
                }
            }
            return [...new Set(filterTextSizeClassNames(classAttrWithinSelection))];
        };
        const getCurrentTextSize = () => {
            var _a;
            const textSizeClassnames = getTextSizeClassnamesWithinSelection();
            if (textSizeClassnames.length > 1)
                return null;
            return (((_a = Object.entries(textSizeExtras).find(([_textSize, { className }]) => className === textSizeClassnames[0])) === null || _a === void 0 ? void 0 : _a[0]) || defaultTextSize);
        };
        const determineCurrentTextSize = () => {
            setCurrentTextSize(getCurrentTextSize());
        };
        editor === null || editor === void 0 ? void 0 : editor.on("selectionUpdate", determineCurrentTextSize);
        editor === null || editor === void 0 ? void 0 : editor.on("transaction", determineCurrentTextSize);
        return () => {
            editor === null || editor === void 0 ? void 0 : editor.off("selectionUpdate", determineCurrentTextSize);
            editor === null || editor === void 0 ? void 0 : editor.off("transaction", determineCurrentTextSize);
        };
    }, [defaultTextSize, editor, theme]);
    return (_jsx(BaseSelect, { label: "", options: textSizeOptions, className: "!border-none !outline-none", menuClassName: "!w-[410px] !h-330px", menuOffset: menuOffset, value: textSizeOptions.find((option) => option.value === currentTextSize), selectionType: "single", onOpenChange: (isOpen) => setIsOpen(isOpen), onChange: (option) => {
            if (option === null || option === void 0 ? void 0 : option.value) {
                editor === null || editor === void 0 ? void 0 : editor.commands.setTextSize(option.value);
                // focus doesn't work without a timeout here - maybe the select is stealing focus?
                setTimeout(() => editor === null || editor === void 0 ? void 0 : editor.commands.focus(), 0);
            }
        }, customTrigger: _jsxs("button", { className: classNames("!rounded-none pr-1 hover:!bg-general-gray-quaternary p-1 cursor-pointer outline-none flex gap-[4px] self-stretch items-center", isOpen &&
                "!bg-general-gray-tertiary hover:!bg-general-gray-tertiary"), type: "button", tabIndex: 0, children: [_jsx("span", { className: "text-sm", children: theme.translate(currentTextSize
                        ? textSizeExtras[currentTextSize].labelKey
                        : "components.rich_text_editor.text_size.multiple_sizes") }), _jsx("span", { className: "w-1 h-1 inline-flex mb-[4px]", children: isOpen ? _jsx(ChevronUpIcon, {}) : _jsx(ChevronDownIcon, {}) })] }), renderListItemContent: ({ value }) => (_jsx(CustomListItemContent, { value: value, defaultTextSize: defaultTextSize })) }));
};
//# sourceMappingURL=TextSizeDropdown.js.map